import { IconNames } from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { client as elevioClient } from 'elevio';
import { ampli } from 'lib-shared-tracking';
import { memo, useCallback, useMemo } from 'react';

import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

import { SidebarFooterButton } from './components/button/sidebarFooterButton';
import { SidebarFooterPopover } from './components/popover/sidebarFooterPopover';
import styles from './sidebarFooter.module.scss';
import { useBeamer } from './utils/useBeamer';

export type SidebarFooterAction = {
  className?: string;
  icon: IconNames;
  label: string;
  onClick: VoidFunction;
};

type SidebarFooterProps = {
  isExpanded: boolean;
};

export const SidebarFooter = memo(function SidebarFooter({
  isExpanded,
}: SidebarFooterProps): JSX.Element {
  const flags = useFeatureFlag(['has-chat-with-dixa-support']);

  const { showBeamer } = useBeamer();

  const handleShowHelpCenter = useCallback(() => {
    elevioClient.open();
    ampli.topbarSupportOpened();
  }, []);

  const handleShowMessenger = useCallback(() => {
    if (flags.hasChatWithDixaSupport) {
      window._dixa_?.invoke('setWidgetOpen', true);
    }
  }, [flags.hasChatWithDixaSupport]);

  const footerActions = useMemo<SidebarFooterAction[]>(() => {
    const actions: SidebarFooterAction[] = [
      {
        icon: 'gift',
        label: "What's new",
        onClick: showBeamer,
        className: 'beamer-button',
      },
      {
        icon: 'question-filled',
        label: 'Help center',
        onClick: handleShowHelpCenter,
      },
    ];

    if (flags.hasChatWithDixaSupport) {
      actions.push({
        icon: 'chat',
        label: 'Chat with support',
        onClick: handleShowMessenger,
      });
    }

    return actions;
  }, [
    showBeamer,
    handleShowHelpCenter,
    handleShowMessenger,
    flags.hasChatWithDixaSupport,
  ]);

  return (
    <ul
      className={classNames(styles.sidebarFooter, {
        [styles.sidebarFooterExpanded]: isExpanded,
      })}
    >
      {isExpanded ? (
        <li>
          {footerActions.map((action) => (
            <SidebarFooterButton key={action.icon} {...action} />
          ))}
        </li>
      ) : (
        <li>
          <SidebarFooterPopover actions={footerActions} />
        </li>
      )}
    </ul>
  );
});
